/**
 * Mock Razorpay authorize page.
 * The "sub-merchant" sees this and approves/denies access for the partner application.
 * Mirrors https://auth.razorpay.com/authorize
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Sparkle, CheckCircle, XCircle, ShieldCheck, Buildings } from "@phosphor-icons/react";

export default function OAuthAuthorize() {
  const [params] = useSearchParams();
  const client_id = params.get("client_id");
  const redirect_uri = params.get("redirect_uri");
  const scope = params.get("scope") || "read_only";
  const state = params.get("state") || "";

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submerchantBiz, setSubmerchantBiz] = useState("Acme Industries Pvt Ltd");
  const [submerchantEmail, setSubmerchantEmail] = useState("merchant@acme.in");
  const [mode, setMode] = useState("test");

  useEffect(() => {
    if (!client_id || !redirect_uri) { setError("Missing client_id or redirect_uri"); return; }
    api.get("/oauth/authorize/preview", { params: { client_id, redirect_uri, scope, state } })
      .then(r => setPreview(r.data))
      .catch(e => setError(formatApiError(e.response?.data?.detail) || "Invalid authorize request"));
  }, [client_id, redirect_uri, scope, state]);

  const approve = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/oauth/approve", {
        client_id, redirect_uri, state, scope,
        submerchant_email: submerchantEmail,
        submerchant_business: submerchantBiz,
        mode,
      });
      // Redirect to redirect_uri with code+state (mirroring Razorpay's behavior)
      const u = new URL(redirect_uri);
      u.searchParams.set("code", data.code);
      u.searchParams.set("state", data.state);
      window.location.href = u.toString();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const deny = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/oauth/deny", { client_id, redirect_uri, state });
      const u = new URL(redirect_uri);
      Object.entries(data).forEach(([k, v]) => u.searchParams.set(k, v));
      window.location.href = u.toString();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (error) return (
    <ErrorScreen title="Cannot authorize" message={error} />
  );
  if (!preview) return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50 grid-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-5 flex items-center justify-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FF6B00]">
            <Sparkle weight="fill" size={14} className="text-white" />
          </div>
          <span className="font-display text-sm font-bold">Razorpay <span className="text-[#FF6B00]">OAuth</span></span>
          {preview.client_env === "development" && (
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Sandbox</span>
          )}
        </div>

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm" data-testid="authorize-card">
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF3E0]">
              <Buildings size={26} weight="duotone" className="text-[#FF6B00]" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-[#0F172A]">
              <span className="text-[#FF6B00]">{preview.application.name}</span> wants to access your Razorpay account
            </h1>
            <p className="mt-1.5 text-sm text-slate-500">
              <a href={preview.application.website} target="_blank" rel="noreferrer" className="hover:underline">{preview.application.website}</a>
            </p>
          </div>

          <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-slate-50 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">This will allow them to</div>
            <ul className="mt-2 space-y-2">
              {preview.scopes.map(s => (
                <li key={s} className="flex items-center gap-2 text-sm">
                  <ShieldCheck size={16} weight="fill" className="text-[#10B981]" />
                  <span className="font-mono text-xs">{s}</span>
                  <span className="text-slate-500 text-xs">— {scopeDesc(s)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mock sub-merchant identity */}
          <div className="mt-5 rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-xs">
            <div className="font-semibold text-amber-800 mb-2">Mock sub-merchant identity (you can edit before approving)</div>
            <div className="grid grid-cols-2 gap-2">
              <input value={submerchantBiz} onChange={(e) => setSubmerchantBiz(e.target.value)} placeholder="Business name" data-testid="submerchant-business"
                className="rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-amber-500" />
              <input value={submerchantEmail} onChange={(e) => setSubmerchantEmail(e.target.value)} placeholder="Email" data-testid="submerchant-email"
                className="rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-amber-500" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-amber-700">Mode:</span>
              {["test", "live"].map(m => (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`submerchant-mode-${m}`}
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${mode === m ? (m === "live" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-[#FF6B00] text-white border-[#FF6B00]") : "border-amber-200 text-amber-800 bg-white"}`}>
                  {m}
                </button>
              ))}
              {preview.client_env === "production" && <span className="text-[10px] text-amber-700">(production clients are forced live)</span>}
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button type="button" onClick={deny} disabled={submitting} data-testid="authorize-deny-button"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              <XCircle size={14} /> Deny
            </button>
            <button type="button" onClick={approve} disabled={submitting} data-testid="authorize-approve-button"
              className="flex-1 btn-primary inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60">
              <CheckCircle size={14} weight="fill" /> {submitting ? "Authorizing…" : "Approve & continue"}
            </button>
          </div>

          <p className="mt-4 text-center text-[11px] text-slate-500">
            By approving, the application can act on your account within the granted scope. You can revoke access from the Razorpay Dashboard at any time.
          </p>
        </div>
      </div>
    </div>
  );
}

function scopeDesc(s) {
  return {
    read_only: "View payments, refunds and other resources",
    read_write: "View and create payments, refunds, orders",
    rx_read_only: "View RazorpayX payouts and contacts",
    rx_read_write: "View and create RazorpayX payouts",
    rx_partner_read_write: "Approve or reject payouts",
  }[s] || "Resource access";
}

function ErrorScreen({ title, message }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md rounded-xl border border-red-200 bg-white p-8 text-center">
        <XCircle weight="fill" size={36} className="mx-auto text-red-500" />
        <h2 className="mt-3 font-display text-xl font-bold">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{message}</p>
      </div>
    </div>
  );
}
